/**
 * GA4-friendly dataLayer pushes for GTM. Keeps ecommerce object usage consistent
 * (clear previous ecommerce before a new hit per Google’s recommendation).
 */

function ensureDataLayer() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function buildMetaPayload({ currency = "USD", value, items = [], transaction_id }) {
  const contents = items.map((item) => ({
    id: String(item.item_id ?? item.id ?? item.item_name ?? ""),
    quantity: Number(item.quantity ?? 1),
    item_price: Number(item.price ?? 0),
  }));

  const payload = {
    content_type: "product",
    content_ids: contents.map((c) => c.id).filter(Boolean),
    contents,
    num_items: contents.reduce((sum, c) => sum + Number(c.quantity || 0), 0),
    value: Number(value),
    currency,
  };

  if (transaction_id != null && String(transaction_id).trim()) {
    payload.transaction_id = String(transaction_id).trim();
  }

  return payload;
}

export function clearEcommerceObject() {
  const dl = ensureDataLayer();
  if (!dl) return;
  dl.push({ ecommerce: null });
}

/**
 * @param {object} params
 * @param {string} [params.currency] ISO 4217, default USD
 * @param {number} params.value Total checkout value (e.g. amount due including fees)
 * @param {object[]} params.items GA4 item lines: item_id, item_name, price, quantity, optional item_category
 * @param {Record<string, unknown>} [params.context] Extra keys at the root of the dataLayer object for GTM (e.g. event_listing_id)
 */
export function pushBeginCheckout({ currency = "USD", value, items = [], context = {} }) {
  const dl = ensureDataLayer();
  if (!dl) return;
  if (!items.length) return;

  clearEcommerceObject();
  dl.push({
    event: "begin_checkout",
    ecommerce: {
      currency,
      value: Number(value),
      items,
    },
    meta: buildMetaPayload({ currency, value, items }),
    ...context,
  });
}

/**
 * Best-effort ID for GA4 transaction_id from buyTickets API responses.
 */
export function resolveGa4TransactionId(response) {
  if (!response || typeof response !== "object") return null;

  const pi = response.paymentIntent ?? response.payment_intent;
  if (pi && typeof pi === "object" && pi.id) return String(pi.id);

  for (const key of ["chargeId", "charge_id", "order_id", "orderId", "transaction_id", "transactionId", "_id", "id"]) {
    const v = response[key];
    if (v != null && String(v).length) return String(v);
  }

  if (response.user != null && response.event != null) {
    const u = response.user;
    const uid = typeof u === "object" && u !== null && u._id != null ? u._id : u;
    const ev = response.event;
    const eid = typeof ev === "object" && ev !== null && ev._id != null ? ev._id : ev;
    return `ots_${uid}_${eid}`;
  }

  return null;
}

/**
 * @param {object} params
 * @param {string} params.transaction_id Required for deduplication in GA4
 * @param {string} [params.currency]
 * @param {number} params.value
 * @param {object[]} params.items
 * @param {Record<string, unknown>} [params.context]
 */
export function pushPurchase({
  currency = "USD",
  value,
  transaction_id,
  items = [],
  context = {},
}) {
  const dl = ensureDataLayer();
  if (!dl) return;

  const tid = transaction_id != null ? String(transaction_id).trim() : "";
  if (!tid || !items.length) return;

  clearEcommerceObject();
  dl.push({
    event: "purchase",
    ecommerce: {
      transaction_id: tid,
      value: Number(value),
      currency,
      items,
    },
    meta: buildMetaPayload({
      currency,
      value,
      items,
      transaction_id: tid,
    }),
    ...context,
  });
}
